import { useCallback, useContext, useMemo } from "react";
import type { CallOptions } from "@connectrpc/connect";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { OrgContext } from "contexts/OrgContext";
import { ExternalNodeService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/external_node_connect";
import {
  ConnectivityInfo,
  ExternalNode as ExternalNodeMessage,
  ListNodesOptions,
} from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/external_node_pb";
import {
  NetworkMemberType,
  Port,
} from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/network_pb";
import { createConnectClient } from "server/connect";

export const DGX_SPARK_DISPLAY_NAME = "DGX Spark";

const externalNodeClient = createConnectClient(ExternalNodeService);
const NODE_LIST_PAGE_SIZE = 100;

type ExternalNode = Awaited<
  ReturnType<typeof externalNodeClient.listNodes>
>["items"][number];

export type { ExternalNode };

export const getExternalNodesQueryKey = (organizationId: string | undefined) =>
  ["external-nodes", organizationId] as const;

const getExternalNodesConnectivityQueryKey = (
  organizationId: string | undefined,
) => ["external-nodes-connectivity", organizationId] as const;

export async function listAllNodes(
  organizationId: string,
  excludeConnectivityInfo: boolean,
  options?: CallOptions,
) {
  const nodes: ExternalNode[] = [];
  const seenPageTokens = new Set<string>();
  let pageToken = "";

  do {
    const response = await externalNodeClient.listNodes(
      {
        organizationId,
        pageParams: {
          pageSize: NODE_LIST_PAGE_SIZE,
          pageToken,
        },
        options: new ListNodesOptions({ excludeConnectivityInfo }),
      },
      options,
    );
    nodes.push(...response.items);

    if (!response.nextPageToken || seenPageTokens.has(response.nextPageToken)) {
      break;
    }
    seenPageTokens.add(response.nextPageToken);
    pageToken = response.nextPageToken;
  } while (pageToken);

  return nodes;
}

export const useNodes = (enabled: boolean = true) => {
  const orgContext = useContext(OrgContext);
  const orgId = orgContext.activeOrgId;
  const queryClient = useQueryClient();
  // Fast pass: rows without connectivity so the list renders immediately.
  const {
    data: fastNodes = [],
    error,
    isInitialLoading,
  } = useQuery({
    queryKey: getExternalNodesQueryKey(orgId),
    queryFn: () => listAllNodes(orgId!, true),
    enabled: enabled && Boolean(orgId),
    staleTime: 30_000,
  });

  // Slow pass: full nodes with connectivity info, once rows are on screen.
  const { data: connectivityNodes = [], isLoading: isConnectivityLoading } =
    useQuery({
      queryKey: getExternalNodesConnectivityQueryKey(orgId),
      queryFn: () => listAllNodes(orgId!, false),
      enabled: enabled && Boolean(orgId) && fastNodes.length > 0,
      staleTime: 30_000,
    });

  const connectivityByNodeId = useMemo(() => {
    const byNodeId = new Map<string, ConnectivityInfo>();
    for (const node of connectivityNodes) {
      if (node.connectivityInfo) {
        byNodeId.set(node.externalNodeId, node.connectivityInfo);
      }
    }
    return byNodeId;
  }, [connectivityNodes]);

  // Keep the fast rows; overlay connectivity once the slow pass resolves.
  const externalNodes = useMemo(
    () =>
      fastNodes.map((node) => {
        const connectivityInfo = connectivityByNodeId.get(node.externalNodeId);
        return connectivityInfo
          ? new ExternalNodeMessage({ ...node, connectivityInfo })
          : node;
      }),
    [fastNodes, connectivityByNodeId],
  );

  const refetch = useCallback(() => {
    return Promise.all([
      queryClient.invalidateQueries({
        queryKey: getExternalNodesQueryKey(orgId),
      }),
      queryClient.invalidateQueries({
        queryKey: getExternalNodesConnectivityQueryKey(orgId),
      }),
    ]);
  }, [orgId, queryClient]);

  const removeNode = useCallback(
    async (externalNodeId: string) => {
      if (!orgId) return;
      await externalNodeClient.removeNode({ externalNodeId });
      queryClient.setQueryData<ExternalNode[]>(
        getExternalNodesQueryKey(orgId),
        (nodes = []) =>
          nodes.filter((node) => node.externalNodeId !== externalNodeId),
      );
    },
    [orgId, queryClient],
  );

  const updateNodeNetworkMemberType = useCallback(
    (externalNodeId: string, networkMemberType: NetworkMemberType) => {
      queryClient.setQueryData<ExternalNode[]>(
        getExternalNodesQueryKey(orgId),
        (nodes = []) =>
          nodes.map((node) =>
            node.externalNodeId === externalNodeId
              ? new ExternalNodeMessage({ ...node, networkMemberType })
              : node,
          ),
      );
    },
    [orgId, queryClient],
  );

  const setHTTPPortAccess = useCallback(
    async (
      portId: string,
      authorizedEmails: string[],
      allowPublicUnauthenticated: boolean,
    ) => {
      if (!orgId) return;
      await externalNodeClient.setHTTPPortAccess({
        portId,
        authorizedEmails,
        allowPublicUnauthenticated,
      });
      queryClient.setQueryData<ExternalNode[]>(
        getExternalNodesQueryKey(orgId),
        (nodes = []) =>
          nodes.map(
            (node) =>
              new ExternalNodeMessage({
                ...node,
                ports: node.ports.map((port) =>
                  port.portId === portId
                    ? new Port({
                        ...port,
                        authorizedEmails,
                        allowPublicUnauthenticated,
                      })
                    : port,
                ),
              }),
          ),
      );
      await queryClient.invalidateQueries({
        queryKey: getExternalNodesQueryKey(orgId),
      });
    },
    [orgId, queryClient],
  );

  return {
    externalNodes,
    isLoading: isInitialLoading,
    isConnectivityLoading,
    error: externalNodes.length === 0 ? error : null,
    refetch,
    removeNode,
    updateNodeNetworkMemberType,
    setHTTPPortAccess,
  };
};
